import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Grid, Container, Stack, Typography, Alert, CircularProgress } from '@material-ui/core';
import { useParams, useSearchParams } from 'react-router-dom';
import Page from '../components/Page';
import Breadcrumb from '../components/common/Breadcrumb';
import MonthPicker, { initialMonthFromUrl } from '../components/billing/MonthPicker';
import DeploymentsKpiRow from '../components/billing/DeploymentsKpiRow';
import DeploymentsPeakUsageAndTableCard from '../components/billing/DeploymentsPeakUsageAndTableCard';
import useCompanyBillingReport from '../hooks/useCompanyBillingReport';
import {
  computePeakConcurrentByDay,
  toBaseDeploymentRow,
  wasActiveOnDay,
  DEPLOYMENT_TABLE_COLUMNS
} from '../utils/billing';

// Same treatment derivation lives in src/utils/billing#deriveTreatment —
// kept here only as a comment for reviewers because the team column
// derivation is local to this page.

// Map an API deployment into the UI row shape used by the shared
// `DeploymentsTableCard`. We override the `id` from
// `toBaseDeploymentRow` so React keys stay unique across teams (the
// shared helper only sees one team's deployments at a time).
function toDeploymentRow(teamName, deployment) {
  return {
    ...toBaseDeploymentRow(deployment),
    teamName,
    id: `${teamName}-${deployment.vrack_name}-${deployment.provision_date}`
  };
}

// Walk every team's billable_parts until we find the one with the
// matching part_number, then flatten its deployments. The API nests
// deployments under each team, so the company-wide view is the
// concatenation of every team's deployments of that part.
function findDeploymentsForPartNumber(company, partNumber) {
  const teams = company?.teams || [];
  const rows = [];
  teams.forEach((team) => {
    const part = (team.billableParts || []).find((p) => p.part_number === partNumber);
    if (!part) return;
    (part.deployments || []).forEach((deployment) => {
      rows.push(toDeploymentRow(team.id, deployment));
    });
  });
  return rows;
}

CompanyPartNumberDeployments.propTypes = {
  companyId: PropTypes.string,
  partNumber: PropTypes.string
};

CompanyPartNumberDeployments.defaultProps = {
  companyId: null,
  partNumber: null
};

export default function CompanyPartNumberDeployments({
  companyId: companyIdProp,
  partNumber: partNumberProp
}) {
  // Route params via useParams; props accepted for tests/storybook.
  const params = useParams();
  const companyId = companyIdProp || params.companyId;
  const partNumber = partNumberProp || params.partNumber;

  // Seed the MonthPicker from the URL `?month=YYYY-MM`. The parent
  // (CompanyBillingReport) appends the param to its navigate URL so the
  // selected month travels with the click; a deep link or browser refresh
  // also lands on the requested month. Falls back to the current month when
  // the param is missing or malformed (initialMonthFromUrl handles that).
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(() => initialMonthFromUrl(searchParams));
  // Same role as on the team page: the chart's active-day filter
  // lives here so a stale selection can't survive a month change.
  const [activeDay, setActiveDay] = useState(null);
  // Reuse the same hook that drives the company billing report -- keeps
  // the fetch, 404 handling, and 404 stubbing consistent with the rest
  // of the page and avoids a second consumer pointing at the same
  // endpoint.
  const { company, loading, error, invoiceNotFound } = useCompanyBillingReport({
    companyId,
    month: selectedMonth
  });

  const billingMonthLabel = selectedMonth ? format(selectedMonth, 'MMMM yyyy') : '';

  // The hook resets `company` to null on each new fetch, so we may have
  // a transient `null` even when a 404 hasn't happened. Build a minimal
  // stub for the 404 case so the page renders chrome + empty fields
  // instead of falling back to the company-not-found copy -- the company
  // itself exists; only the invoice is missing.
  const showInvoiceNotFound = invoiceNotFound && !loading;
  const displayCompany = useMemo(() => {
    if (company) return company;
    if (!showInvoiceNotFound) return null;
    return {
      id: companyId,
      name: companyId,
      invoiceNumber: '',
      monthlyCharges: 0,
      platformFee: 0,
      teamUsageCost: 0,
      teamsCount: 0,
      billableResources: 0,
      partNumbers: [],
      teams: []
    };
  }, [company, showInvoiceNotFound, companyId]);

  const deployments = useMemo(
    () => findDeploymentsForPartNumber(displayCompany, partNumber),
    [displayCompany, partNumber]
  );

  // Compute the per-day peak instants once so the row highlight can
  // filter to racks alive at the bar's peak (not every rack that
  // overlapped the day window). The chart's bar height is the max
  // concurrent at some instant during the day; on busy days the
  // overlap set is wider than the peak-instant set, so we have to
  // resolve the peak instant here to keep the table in sync.
  const peakInstantsByDay = useMemo(() => {
    const result = computePeakConcurrentByDay(deployments, selectedMonth);
    if (!result) return {};
    const map = {};
    result.days.forEach((dayIso, idx) => {
      map[dayIso] = result.peakInstants[idx];
    });
    return map;
  }, [deployments, selectedMonth]);

  // Apply the chart's active-day filter on top of the company-wide
  // deployments. Without `activeDay` this is a no-op. When the user
  // has clicked a day, we show every rack that overlapped the day
  // window -- the chart's bar is the peak instant, but the table
  // should still show every rack that was alive at some point on
  // that day so the user has full context. The highlight rule
  // (driven by `wasConcurrentOnDay` in `DeploymentsTableCard`)
  // distinguishes the contributing racks from the merely-overlapping
  // ones visually.
  const filteredDeployments = useMemo(() => {
    if (!activeDay) return deployments;
    return deployments.filter((d) => wasActiveOnDay(d.createdDate, d.deletedDate, activeDay));
  }, [deployments, activeDay]);

  // Reset the active-day filter when the user picks a different
  // month -- a day from the previous month has no meaning on the new
  // one. No data fetching happens here (the company page uses
  // `useCompanyBillingReport`), so a plain effect is enough.
  useEffect(() => {
    setActiveDay(null);
  }, [selectedMonth]);

  // Error path runs before the loading guard: if the API has reported a
  // real failure, the user should see the error alert regardless of
  // whether `company` happens to be null. Otherwise the loading spinner
  // short-circuits on a null `displayCompany` and hides the actual
  // problem.
  if (error && !invoiceNotFound) {
    return (
      <Page title="Error">
        <Container maxWidth={false}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load billing data.
          </Alert>
        </Container>
      </Page>
    );
  }

  // useVrackApi's `loading` flag starts at false and only flips to true
  // once fetchData is actually called, so on first mount `loading` is
  // still false while `company` is also null. The loading guard below
  // covers both that pre-fetch window and the in-flight window — any
  // time we don't have a displayCompany and we're not in the 404/error
  // branches, show the spinner rather than falling through to chrome
  // that reads `displayCompany.name` and crashes.
  if (!displayCompany && !invoiceNotFound) {
    return (
      <Page title="Loading...">
        <Container maxWidth={false}>
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${partNumber} Deployments | Virtual Racks`}>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {/* Breadcrumb on the company-scoped URL renders
              Home -> Billing -> {companyId} -> {partNumber}. The `company`
              and `part` route segments are delimiters and excluded from
              the trail so they don't render as clickable crumbs. */}
          <Grid item xs={12}>
            <Breadcrumb
              labels={{ [partNumber]: partNumber }}
              exclude={['company', 'part']}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Page header -- overline + h4 mirrors the company billing
              report and the team-scoped deployments page so all three
              detail pages share a single visual language. */}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Stack>
                <Typography variant="overline" color="primary">
                  Part Number Deployments
                </Typography>
                <Typography variant="h4">{partNumber}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
              </Stack>
            </Stack>
          </Grid>

          {/* On a 404, fall through to chrome with empty values rather
              than showing the generic error page -- the company still
              exists, there's just no invoice for the selected month yet. */}
          {showInvoiceNotFound && (
            <Grid item xs={12}>
              <Alert severity="info" data-testid="invoice-not-found-alert">
                No invoice has been generated for {displayCompany.name} in {billingMonthLabel}.
                Generate one on the company billing report and this page will populate as soon as
                the invoice is available.
              </Alert>
            </Grid>
          )}

          {/* KPI cards */}
          <Grid item xs={12}>
            <DeploymentsKpiRow
              partNumber={partNumber}
              deployments={deployments}
              month={selectedMonth}
            />
          </Grid>

          {/* Combined peak-usage + deployments card. Same component
              as the team-scoped page so the two detail views share
              the same visual language; team attribution lives in the
              table below (showTeamColumn=true). */}
          <Grid item xs={12}>
            <DeploymentsPeakUsageAndTableCard
              deployments={deployments}
              filteredDeployments={filteredDeployments}
              month={selectedMonth}
              activeDay={activeDay}
              peakInstant={activeDay ? peakInstantsByDay[activeDay] : null}
              onActiveDayChange={setActiveDay}
              partNumber={partNumber}
              description="Peak concurrent units in use, measured per day. Click a day to filter the deployments to racks active on that day."
              emptyChartMessage={`No deployments of ${partNumber} recorded across ${displayCompany.name} in ${billingMonthLabel}.`}
              emptyTableMessage={`No deployments recorded for ${partNumber} in ${billingMonthLabel}.`}
              headLabel={DEPLOYMENT_TABLE_COLUMNS}
              showTeamColumn
              defaultOrderBy="createdDate"
              defaultOrder="desc"
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
